import { useContext, useEffect, useCallback, useMemo, useRef } from 'react';
import propTypes from 'prop-types';
import { useReducer, useQuery as useQueryString } from '@kiper/hooks';
import { useQuery, useMutation } from 'react-apollo';
import { get, setMap, types, remove } from '@kiper/cookie';
import { apolloErrorHandler } from '@kiper/fns';
import authContext from './context';

import {
  actionTypes,
  setRedirectUri as setRedirectUriAction,
  resetFlags as resetFlagsAction,
  signInV2Mutation,
  signInChallengeMutation,
  registerWorkstationMutation,
  respondMfaMutation,
  associateTokenMutation,
  verifyTokenMutation,
  associateMfaEnrollmentMutation,
  verifyMfaEnrollmentMutation,
  whoAmI as whoAmIGql,
  forgotMutation,
  resetMutation,
  renewMutation,
} from './actions';

import authReducer from './reducer';
import {
  getLocalKeyPair,
  ensureKeyPair,
  rotateKeyPair,
  signWithLocalKey,
  getWorkstationInfo,
  normalizeWorkstationAuthStatus,
  WORKSTATION_AUTH_STATUS,
} from '../workstationAuth';

function AuthProvider({ children }) {
  const query = useQueryString();
  const [state, dispatch] = useReducer(authReducer, useContext(authContext));

  useQuery(whoAmIGql, {
    skip: !get(types.authorization),
    fetchPolicy: 'cache-first',
  });

  const [loginV2Fc] = useMutation(signInV2Mutation);
  const [challengeFc] = useMutation(signInChallengeMutation);
  const [registerWorkstationFc] = useMutation(registerWorkstationMutation);
  const [respondMfaFc] = useMutation(respondMfaMutation);
  const [associateTokenFc] = useMutation(associateTokenMutation);
  const [verifyTokenFc] = useMutation(verifyTokenMutation);
  const [associateMfaEnrollmentFc] = useMutation(
    associateMfaEnrollmentMutation,
  );
  const [verifyMfaEnrollmentFc] = useMutation(verifyMfaEnrollmentMutation);

  // Guarda a prova de posse (thumbprint + assinatura do nonce) computada no
  // signIn, para reenviar no respondMfa. Quem tem TOTP cadastrado e está
  // sujeito ao portão cai no desafio MFA antes do portão ser avaliado — o
  // nonce não é consumido nesse caminho, então a mesma assinatura serve para
  // os dois passos.
  const mfaProofRef = useRef({
    publicKeyThumbprint: undefined,
    signature: undefined,
  });

  // SignIn v2 (monitoring). A flag do checkUserExist decide o gate de workstation:
  //   false → v2 sem assinatura
  //   true  → challenge → assina → v2 com gate WebCrypto
  const login = useCallback(
    async ({ username, password, workstationAuthorizationRequired }) => {
      dispatch({ type: actionTypes.login.request });

      // Reseta a prova de posse a cada tentativa — evita reaproveitar a
      // assinatura de um login anterior (ex.: usuário fora do piloto, ou
      // troca de conta na mesma aba).
      mfaProofRef.current = {
        publicKeyThumbprint: undefined,
        signature: undefined,
      };

      if (!workstationAuthorizationRequired) {
        const { data } = await loginV2Fc({
          variables: { credentials: { username, password } },
        });
        const signIn = data?.signInV2;
        // Sem accessToken → não grava cookie.
        if (signIn?.accessToken) {
          setMap({
            [types.authorization]: signIn.accessToken,
            [types.refresh]: signIn.refreshToken,
          });
          dispatch({ type: actionTypes.login.success, payload: signIn });
        }
        // O BE decide o portão pelo perfil, não pela flag do checkUserExist.
        // Se as duas divergirem, devolver o status real evita o login parar
        // em silêncio: a máquina de estados roteia o Pending/Rejected.
        return {
          status: normalizeWorkstationAuthStatus(
            signIn?.workstationAuthorization?.status,
          ),
          signIn,
        };
      }

      let nonce = null;
      try {
        const { data } = await challengeFc({ variables: { username } });
        nonce = data?.signInChallenge?.nonce || null;
      } catch (error) {
        // Falha no challenge faz o v2 cair em Rejected — a UI oferece cadastro.
      }

      let publicKeyThumbprint;
      let signature;
      if (nonce) {
        const keyPair = await getLocalKeyPair();
        if (keyPair) {
          publicKeyThumbprint = keyPair.thumbprint;
          signature = await signWithLocalKey(keyPair.privateKey, nonce);
        }
      }

      mfaProofRef.current = { publicKeyThumbprint, signature };

      const { data } = await loginV2Fc({
        variables: {
          credentials: { username, password, publicKeyThumbprint, signature },
        },
      });

      const signIn = data?.signInV2;
      const status = normalizeWorkstationAuthStatus(
        signIn?.workstationAuthorization?.status,
      );

      if (status === WORKSTATION_AUTH_STATUS.APPROVED && signIn?.accessToken) {
        setMap({
          [types.authorization]: signIn.accessToken,
          [types.refresh]: signIn.refreshToken,
        });
        dispatch({ type: actionTypes.login.success, payload: signIn });
      }

      return { status, signIn };
    },
    [],
  );

  // `rotateKey` chega true quando a pessoa pede nova aprovação para uma estação
  // que o backend já recusou: a chave local não vale mais, então é trocada antes
  // do cadastro para a solicitação nascer com um thumbprint novo. No primeiro
  // acesso (sem chave local) segue false e o comportamento é o de sempre.
  const registerWorkstation = useCallback(
    async ({
      username,
      latitude,
      longitude,
      partnerContextId,
      condominiumContextId,
      rotateKey = false,
    }) => {
      const keyPair = rotateKey ? await rotateKeyPair() : await ensureKeyPair();
      const { data } = await registerWorkstationFc({
        variables: {
          input: {
            // E-mail enviado apenas para auditoria — não faz parte da
            // identidade da estação de trabalho, que é definida pelo thumbprint.
            email: username,
            publicKey: keyPair.publicKeyJwk,
            publicKeyThumbprint: keyPair.thumbprint,
            latitude,
            longitude,
            workstationInfo: getWorkstationInfo(),
            // Exatamente um dos dois chega preenchido — quem decide é
            // useWorkstationRegistration, que nunca envia os dois juntos.
            partnerContextId,
            condominiumContextId,
          },
        },
      });
      const status = normalizeWorkstationAuthStatus(
        data?.registerWorkstation?.status,
      );
      return status;
    },
    [],
  );

  // Desafio: sessão + código → grava os tokens. Reenvia a prova de posse
  // guardada no signIn (mesmo thumbprint/assinatura) para quem está sujeito
  // ao portão de workstation: o BE avalia o portão aqui também, e a resposta
  // traz `workstationAuthorization` para o front cair na mesma máquina de
  // estados do signIn barrado quando a estação não estiver aprovada.
  const respondMfa = useCallback(async ({ session, code, username }) => {
    const { publicKeyThumbprint, signature } = mfaProofRef.current;
    const { data } = await respondMfaFc({
      variables: { session, code, username, publicKeyThumbprint, signature },
    });
    const signIn = data?.respondMfa;
    if (signIn?.accessToken) {
      setMap({
        [types.authorization]: signIn.accessToken,
        [types.refresh]: signIn.refreshToken,
      });
      dispatch({ type: actionTypes.login.success, payload: signIn });
    }
    const status = normalizeWorkstationAuthStatus(
      signIn?.workstationAuthorization?.status,
    );
    return { status, signIn };
  }, []);

  // Cadastro: retorna segredo + otpAuthUri para o QR.
  const associateToken = useCallback(async () => {
    const { data } = await associateTokenFc();
    return data?.associateToken;
  }, []);

  // Ativa o autenticador. Mantém os cookies do signin válidos para logar direto.
  const verifyToken = useCallback(async ({ code }) => {
    const { data } = await verifyTokenFc({ variables: { code } });
    return data?.verifyToken;
  }, []);

  // Cadastro obrigatório de MFA: a credencial é a enrollmentSession do signIn.
  const associateMfaEnrollment = useCallback(async enrollmentSession => {
    const { data } = await associateMfaEnrollmentFc({
      variables: { enrollmentSession },
    });
    return data?.associateMfaEnrollment;
  }, []);

  // Ativa o TOTP e grava os cookies definitivos, no mesmo padrão do respondMfa.
  const verifyMfaEnrollment = useCallback(
    async ({ enrollmentSession, code }) => {
      const { data } = await verifyMfaEnrollmentFc({
        variables: { enrollmentSession, code },
      });
      const signIn = data?.verifyMfaEnrollment;
      if (signIn?.accessToken) {
        setMap({
          [types.authorization]: signIn.accessToken,
          [types.refresh]: signIn.refreshToken,
        });
        dispatch({ type: actionTypes.login.success, payload: signIn });
      }
      return signIn;
    },
    [],
  );

  const [forgotFc] = useMutation(forgotMutation, {
    onCompleted: ({ forgot }) => {
      dispatch({
        type: actionTypes.forgot.success,
        payload: forgot,
      });
    },
    onError: error => {
      const formattedError = apolloErrorHandler(error);
      dispatch({ type: actionTypes.forgot.error, payload: formattedError });
    },
  });
  const forgot = useCallback(
    ({ username }) => forgotFc({ variables: { username } }),
    [],
  );

  const [resetFc] = useMutation(resetMutation, {
    onError: e => dispatch({ type: actionTypes.reset.error, payload: e }),
    onCompleted: ({ resetPassword }) => {
      dispatch({
        type: actionTypes.reset.success,
        payload: resetPassword,
      });
    },
  });

  const reset = useCallback(({ username, password, confirmationCode }) => {
    dispatch({ type: actionTypes.reset.request });
    resetFc({
      variables: { resetPassword: { username, password, confirmationCode } },
    });
  }, []);

  const [renewFc] = useMutation(renewMutation, {
    onError: e => dispatch({ type: actionTypes.renew.error, payload: e }),
    onCompleted: ({ renew: renewResult }) => {
      dispatch({ type: actionTypes.renew.success, payload: renewResult });
    },
  });

  const renew = useCallback(
    ({ username, currentPassword, newPassword, mfaCode }) => {
      dispatch({ type: actionTypes.renew.request });
      renewFc({
        variables: {
          renewPassword: { username, currentPassword, newPassword, mfaCode },
        },
      });
    },
    [],
  );

  const goBackLogin = useCallback(() => {
    dispatch({ type: actionTypes.forgot.goBack });
  }, []);

  const resetFlags = useCallback(() => dispatch(resetFlagsAction()), []);

  useEffect(() => {
    dispatch(setRedirectUriAction(query.get('redirect_uri')));
  }, []);

  const signOut = useCallback(() => {
    remove([
      types.authorization,
      types.refresh,
      types.context,
      types.partnerContext,
      types.topNodeId,
      types.topContextId,
    ]);

    window.location = '/';
  }, []);

  const value = useMemo(
    () => ({
      ...state,
      login,
      forgot,
      reset,
      renew,
      resetFlags,
      goBackLogin,
      signOut,
      registerWorkstation,
      respondMfa,
      associateToken,
      verifyToken,
      associateMfaEnrollment,
      verifyMfaEnrollment,
    }),
    [
      state,
      login,
      forgot,
      reset,
      renew,
      resetFlags,
      goBackLogin,
      signOut,
      registerWorkstation,
      respondMfa,
      associateToken,
      verifyToken,
      associateMfaEnrollment,
      verifyMfaEnrollment,
    ],
  );

  return <authContext.Provider value={value}>{children}</authContext.Provider>;
}

export default AuthProvider;

AuthProvider.propTypes = {
  children: propTypes.element.isRequired,
};
